import { useState, useCallback, useEffect, useRef } from 'react'
import { ResponsiveGridLayout, useContainerWidth, verticalCompactor } from 'react-grid-layout'
import AnalyticsQuery from 'AnalyticsDashboard/components/AnalyticsQuery'
import HtmlBlock from 'AnalyticsDashboard/components/HtmlBlock'
import QueryBuilder from 'AnalyticsDashboard/components/QueryBuilder'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

declare const _: any

const GRID_COLS = { lg: 12, md: 12, sm: 6, xs: 6 }
const DEFAULT_W = 6
const DEFAULT_H = 8
const ROW_HEIGHT = 50

interface LayoutItem {
  i: string
  x: number
  y: number
  w: number
  h: number
  minW?: number
  minH?: number
}

interface QueryConfig {
  id: string
  chartType: string
  name?: string
  static?: boolean
  chartOptions?: Record<string, any>
  metrics?: any[]
  dimensions?: any[]
  primaryDimension?: any
  secondaryDimension?: any
  orderBy?: any[]
  limit?: number | null
  overrideFilters?: Record<string, any>
  comparePeriods?: number | null
  layout?: { x: number; y: number; w: number; h: number }
}

interface QueryBuilderPanelProps {
  initialConfig?: any[]
  onChange?: (config: any[]) => void
  readOnly?: boolean
}

function defaultLayoutItem(id: string, index: number, existingLayout: LayoutItem[]): LayoutItem {
  const col = (index % 2) * DEFAULT_W
  const maxY = existingLayout.reduce((max, item) => Math.max(max, item.y + item.h), 0)
  const row = index % 2 === 0 ? maxY : (existingLayout.length > 0 ? existingLayout[existingLayout.length - 1].y : 0)
  return { i: id, x: col, y: row, w: DEFAULT_W, h: DEFAULT_H, minW: 2, minH: 2 }
}

function initState(config: any) {
  if (!Array.isArray(config) || config.length === 0) return { queries: [] as QueryConfig[], layout: [] as LayoutItem[] }
  const queries: QueryConfig[] = config.map((c: any, i: number) => {
    const id = c.id || `user_query_${Date.now()}_${i}`
    return { ...c, id }
  })
  const layout: LayoutItem[] = queries.map((q, i) => {
    if (q.layout) return { ...q.layout, i: q.id, minW: 2, minH: 2 }
    return defaultLayoutItem(q.id, i, [])
  })
  return { queries, layout }
}

function toConfig(queries: QueryConfig[], layout: LayoutItem[]) {
  const layoutMap: Record<string, { x: number; y: number; w: number; h: number }> = {}
  layout.forEach(item => {
    layoutMap[item.i] = { x: item.x, y: item.y, w: item.w, h: item.h }
  })
  return queries.map(({ id, ...rest }) => ({
    ...rest,
    layout: layoutMap[id] || undefined,
  }))
}

export default function QueryBuilderPanel({ initialConfig, onChange, readOnly = false }: QueryBuilderPanelProps) {
  const [{ queries, layout }, setState] = useState(() => initState(initialConfig))
  const [showBuilder, setShowBuilder] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [viewModes, setViewModes] = useState<Record<string, string>>({})
  const isInitialMount = useRef(true)
  const layoutMounted = useRef(false)
  const { width, containerRef } = useContainerWidth()

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (onChange) onChange(toConfig(queries, layout))
  }, [queries, layout])

  const setQueries = useCallback((updater: ((prev: QueryConfig[]) => QueryConfig[]) | QueryConfig[]) => {
    setState(prev => ({
      ...prev,
      queries: typeof updater === 'function' ? updater(prev.queries) : updater,
    }))
  }, [])

  const handleAdd = useCallback((config: any) => {
    const id = `user_query_${Date.now()}`
    setState(prev => {
      const newQueries = [...prev.queries, { ...config, id }]
      const newLayoutItem = defaultLayoutItem(id, prev.queries.length, prev.layout)
      return {
        queries: newQueries,
        layout: [...prev.layout, newLayoutItem],
      }
    })
    setShowBuilder(false)
  }, [])

  const handleUpdate = useCallback((config: any) => {
    setQueries(prev => prev.map(q =>
      q.id === editingId ? { ...config, id: editingId } : q
    ))
    setEditingId(null)
    setShowBuilder(false)
  }, [editingId])

  const handleRemove = useCallback((id: string) => {
    setState(prev => ({
      queries: prev.queries.filter(q => q.id !== id),
      layout: prev.layout.filter(item => item.i !== id),
    }))
  }, [])

  const handleEdit = useCallback((id: string) => {
    setEditingId(id)
    setShowBuilder(true)
  }, [])

  const handleCancel = useCallback(() => {
    setShowBuilder(false)
    setEditingId(null)
  }, [])

  const handleLayoutChange = useCallback((newLayout: LayoutItem[]) => {
    if (!layoutMounted.current) {
      layoutMounted.current = true
      return
    }
    setState(prev => {
      const queryIds = new Set(prev.queries.map(q => q.id))
      const filtered = newLayout.filter(item => queryIds.has(item.i))
      return { ...prev, layout: filtered }
    })
  }, [])

  return (
    <div className="query-builder-panel" ref={containerRef}>
      {!readOnly && (
        <div className="query-builder-panel__toolbar">
          <button
            type="button"
            className="query-builder-panel__add"
            onClick={() => setShowBuilder(true)}
          >
            + Add Query
          </button>
        </div>
      )}

      {queries.length > 0 && width > 0 && (
        <ResponsiveGridLayout
          className="query-builder-panel__grid"
          width={width}
          layouts={{ lg: readOnly ? layout.map(item => ({ ...item, static: true })) : layout }}
          cols={GRID_COLS}
          rowHeight={ROW_HEIGHT}
          dragConfig={readOnly ? undefined : { handle: '.query-builder-panel__drag-handle' }}
          isDraggable={!readOnly}
          isResizable={!readOnly}
          onLayoutChange={readOnly ? undefined : handleLayoutChange}
          compactor={verticalCompactor}
          margin={[16, 16]}
        >
          {queries.map((config) => (
            <div key={config.id} className="query-builder-panel__query">
              <div className="query-builder-panel__query-header">
                {!readOnly && <span className="query-builder-panel__drag-handle" title="Drag to reorder">⠿</span>}
                <span className="query-builder-panel__query-title">
                  {config.name || _.capitalize(config.chartType)}
                </span>
                <div className="query-builder-panel__query-actions">
                  {!config.static && (
                    <select
                      className="query-builder-panel__view-select"
                      value={viewModes[config.id] || 'chart'}
                      onChange={(e) => { const val = e.target.value; setViewModes(prev => ({ ...prev, [config.id]: val })) }}
                    >
                      <option value="chart">Chart</option>
                      <option value="table">Table</option>
                      {!readOnly && <option value="sql">SQL</option>}
                    </select>
                  )}
                  {!readOnly && <button type="button" onClick={() => handleEdit(config.id)}>Edit</button>}
                  {!readOnly && <button type="button" onClick={() => handleRemove(config.id)}>Remove</button>}
                </div>
              </div>
              <div className="query-builder-panel__query-body">
                {config.static ? (
                  <HtmlBlock chartOptions={config.chartOptions} />
                ) : (
                  <AnalyticsQuery
                    key={`${config.id}-${JSON.stringify(config)}`}
                    chartType={config.chartType}
                    chartOptions={config.chartOptions}
                    metrics={config.metrics}
                    dimensions={config.dimensions}
                    primaryDimension={config.primaryDimension}
                    secondaryDimension={config.secondaryDimension}
                    orderBy={config.orderBy}
                    limit={config.limit}
                    overrideFilters={config.overrideFilters}
                    comparePeriods={config.comparePeriods}
                    rawSqlFactTable={config.rawSqlFactTable}
                    assistantConversationId={config.assistantConversationId}
                    viewMode={(viewModes[config.id] || 'chart') as 'chart' | 'table' | 'sql'}
                  />
                )}
              </div>
            </div>
          ))}
        </ResponsiveGridLayout>
      )}

      {!readOnly && queries.length > 0 && (
        <div className="query-builder-panel__toolbar">
          <button
            type="button"
            className="query-builder-panel__add"
            onClick={() => setShowBuilder(true)}
          >
            + Add Query
          </button>
        </div>
      )}

      {!readOnly && showBuilder && (
        <div className="query-builder-panel__modal-overlay" onClick={handleCancel}>
          <div className="query-builder-panel__modal" onClick={(e) => e.stopPropagation()}>
            <QueryBuilder
              key={editingId ? `edit-${editingId}` : 'add'}
              onSubmit={editingId ? handleUpdate : handleAdd}
              onCancel={handleCancel}
              initialConfig={editingId ? queries.find(q => q.id === editingId) : null}
            />
          </div>
        </div>
      )}
    </div>
  )
}
